// react
import React, {
  FC,
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { StyledButton, StyledIcon, StyledPopper } from "components/StyledUI";
import { tss } from "tss-react/mui";

// mui
import { useTheme } from "@mui/material/styles";

import { Typography, Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";

import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as DeutschIcon } from "assets/icons/languages/de.svg";
import { ReactComponent as EnglischIcon } from "assets/icons/languages/en.svg";

import HTMLReactParser from "html-react-parser";
import { useAppDispatch, useAppSelector } from "_redux/hooks";
import { LOCALE } from "_foundation/constants/common";
import { localStorageUtil } from "_foundationExt/utils/storageUtil";
import { useCurrentLanguage } from "i18n";
import { PERMANENT_STORE_DAYS } from "configs/common";
import personService from "_foundation/apis/transaction/person.service";
import { ServerError } from "types/Error";
import { useSite } from "_foundation/hooks/useSite";
import { useOnlineStatus } from "_foundationExt/hooks/useOnlineStatus";
import {
  loginStatusSelector,
  preferredLanguageSelector,
} from "_redux/selectors/user";
import { supportedLanguagesSelector } from "_redux/selectors/site";
import { UPDATE_PREFERRED_LANGUAGE } from "_redux/actions/user";
import { CommerceEnvironment } from "../../../constants/common";

const renderFlagIcon = (locale: string) =>
  ({
    de: <DeutschIcon />,
    en: <EnglischIcon />,
  })[locale] || null;

const useStyles = tss.create(({ theme }) => ({
  button: {
    width: "40px",
    height: "40px",
    padding: 0,
    backgroundColor: "transparent !important",
  },
  link: {
    cursor: "pointer",
  },
  disabledItem: {
    opacity: 0.3,
    pointerEvents: "none",
    cursor: "default",
  },
  content: {
    width: "200px",
    maxWidth: "370px",
    maxHeight: "600px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "310px",
    },
  },
  popperItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2.5),
    "&:hover $icon": {
      backgroundColor: theme.palette.primary.light,
    },
    "& svg": {
      height: "40px",
      width: "50px",
      flexShrink: 0,
    },
    "& .text": {
      marginLeft: theme.spacing(1.5),
    },
    "&:last-child": {
      marginBottom: 0,
    },
  },
  text: {
    marginLeft: theme.spacing(1.5),
  },
}));

interface IconPopper {
  identifier: string;
  current?: boolean;
  text: string;
}

const LanguageSelectionInner: FC = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const { currentSite } = useSite();
  const { isOnline } = useOnlineStatus();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();

  const { currentLanguage } = useCurrentLanguage();
  const dispatch = useAppDispatch();
  const loggedIn = useAppSelector(loginStatusSelector);
  const preferredLanguage = useAppSelector(preferredLanguageSelector);
  const supportedLanguages = useAppSelector(supportedLanguagesSelector);

  const popperContent: IconPopper[] =
    useMemo(
      () =>
        supportedLanguages
          ?.map((langId) => CommerceEnvironment.languageMapShort[langId])
          .filter(Boolean)
          .map((locale) => {
            // Dynamic determination of the text from the translation file
            const text = t(`logon.languages.${locale}`);

            return {
              identifier: locale,
              current: locale === currentLanguage,
              text,
            };
          }),
      [currentLanguage, supportedLanguages, t]
    ) || [];

  const handlePopperClose = useCallback(() => {
    setAnchorEl(undefined);
  }, []);

  const changeLanguage = useCallback(
    (language: string) => {
      // check if language from site default matches the current store language
      if (language !== i18n.languages[0]) {
        // if not then change language
        i18n.changeLanguage(language);
        localStorageUtil.set(LOCALE, language, PERMANENT_STORE_DAYS);
        dispatch(
          UPDATE_PREFERRED_LANGUAGE(
            CommerceEnvironment.languageToLocaleMap[language]
          )
        );

        handlePopperClose();

        // Slight delay to ensure storage and state updates are complete
        const reload = () =>
          setTimeout(() => {
            window.location.reload();
          }, 100);

        const newLanguage = CommerceEnvironment.languageToLocaleMap[language];
        // Update language if no preferredLanguage or if preferredLanguage exists and differs from newLanguage
        const shouldUpdateLanguage =
          currentSite?.storeID &&
          loggedIn &&
          isOnline &&
          (!preferredLanguage ||
            (CommerceEnvironment.localeToLanguageMap[preferredLanguage] !=
              null &&
              preferredLanguage !== newLanguage));

        if (shouldUpdateLanguage) {
          const updatedForm = {
            preferredLanguage: newLanguage,
          };

          const parameters = {
            storeId: currentSite.storeID,
            body: updatedForm,
          };

          personService
            .updatePersonOnUserRegistrationUpdate(parameters)
            .catch((e) => {
              const responseError = e.response?.data.errors[0] as
                | ServerError
                | undefined;
              if (
                responseError &&
                responseError.errorKey === "_ERR_CMD_INVALID_PARAM"
              ) {
                // eslint-disable-next-line no-console
                console.error("Update preferredLanguage on login failed", e);
              }
            })
            .finally(reload);
        } else {
          reload();
        }
      }
    },
    [
      currentSite?.storeID,
      dispatch,
      handlePopperClose,
      i18n,
      isOnline,
      loggedIn,
      preferredLanguage,
    ]
  );

  const handlePopperToggle: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      if (anchorEl == null) {
        setAnchorEl(event.currentTarget);
      } else {
        handlePopperClose();
      }
    },
    [handlePopperClose, anchorEl]
  );

  return (
    <>
      <StyledButton
        className={classes.button}
        aria-describedby="language-flyout"
        onClick={handlePopperToggle}>
        <StyledIcon size="40">{renderFlagIcon(currentLanguage)}</StyledIcon>
      </StyledButton>

      {popperContent && popperContent.length > 1 && (
        <StyledPopper
          id="language-flyout"
          zIndex={theme.zIndex.extension.languageFlyoutPopper}
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={handlePopperClose}
          placement="bottom-end"
          disablePortal>
          <StyledButton
            className="close"
            onClick={handlePopperClose}
            startIcon={<CloseIcon />}
            size="small"
            color="inherit"
            tooltip={t("iconTooltips.closeIcon")}
          />
          <Grid container className={classes.content}>
            <Grid xs={12}>
              {popperContent.map((item) => {
                const isDisabled = item.current;

                return (
                  <Box
                    key={`${item.identifier}-${item.text}`}
                    className={`${classes.popperItem} ${classes.link} ${isDisabled ? classes.disabledItem : ""}`}
                    onClick={() =>
                      !isDisabled && changeLanguage(item.identifier)
                    }
                    role={isDisabled ? "presentation" : "button"}
                    tabIndex={isDisabled ? -1 : 0}
                    onKeyDown={(e) =>
                      !isDisabled &&
                      e.key === "Enter" &&
                      changeLanguage(item.identifier)
                    }
                    aria-label={
                      isDisabled
                        ? `Current language is ${item.text}`
                        : `Change language to ${item.text}`
                    }>
                    {renderFlagIcon(item.identifier)}
                    <Typography className="text">
                      {HTMLReactParser(item.text)}
                    </Typography>
                  </Box>
                );
              })}
            </Grid>
          </Grid>
        </StyledPopper>
      )}
    </>
  );
};

const LanguageSelection: FC = () => {
  const supportedLanguages = useAppSelector(supportedLanguagesSelector);

  if (!supportedLanguages || supportedLanguages.length < 2) {
    return null;
  }

  return <LanguageSelectionInner />;
};

export default LanguageSelection;
